import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, Activity, Thermometer, Droplets, 
  Trophy, Shield, Crown, Stethoscope, Calendar, 
  AlertTriangle, CheckCircle, Flame, Lock, Globe,
  TrendingUp, Zap, Coins, Medal, ChevronDown, ChevronRight, Info,
  RefreshCw, Moon, Smartphone, Target, Clock, Apple, Utensils, Waves,
  Users, Share2, QrCode, MapPin, Star, Award, BarChart3,
  MessageCircle, UserPlus, Flag, Eye, ThumbsUp
} from 'lucide-react';
import MediCureOnSidebar from '../common/MediCureOnSidebar';
import MediCureOnHeader from '../common/MediCureOnHeader';

// Brand colors
const BRAND_COLORS = {
  navy: '#0B1535',
  gold: '#C9A227',
  slate: '#111322',
  teal: '#15B8A6',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',
  primary: '#0B1535',
  secondary: '#15B8A6'
};

const generateVitalSignsData = (days = 30) => {
  const data = [];
  const now = new Date();
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    
    data.push({
      date: date.toISOString().split('T')[0],
      timestamp: date.getTime(),
      heartRate: 68 + Math.random() * 12 + Math.sin(i * 0.1) * 5,
      hrv: 35 + Math.random() * 15 + Math.cos(i * 0.15) * 8,
      bloodPressureSys: 118 + Math.random() * 8 + Math.sin(i * 0.08) * 4,
      bloodPressureDia: 76 + Math.random() * 6 + Math.cos(i * 0.12) * 3,
      spo2: 97.5 + Math.random() * 1.5,
      respiratoryRate: 14 + Math.random() * 4,
      temperature: 98.3 + Math.random() * 0.8,
      glucose: 88 + Math.random() * 20 + Math.sin(i * 0.2) * 8,
      weight: 165 + Math.random() * 1.5 - 0.75,
      sleepHours: 7.2 + Math.random() * 1.8,
      sleepEfficiency: 82 + Math.random() * 15,
      steps: 8500 + Math.random() * 4000,
      activeMinutes: 45 + Math.random() * 35,
      calories: 2250 + Math.random() * 500,
      hydration: 6 + Math.random() * 4,
      stressLevel: 3 + Math.random() * 4
    });
  }
  
  return data;
};

// Professional Elite Challenge Card based on ChallengesTab.jsx patterns
const EliteChallengeCard = ({ challenge }) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Legendary': return BRAND_COLORS.error;
      case 'Master': return '#9333EA'; // Purple
      case 'Expert': return BRAND_COLORS.warning;
      default: return BRAND_COLORS.info;
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'HIGH': return BRAND_COLORS.error;
      case 'MEDIUM': return BRAND_COLORS.warning;
      case 'LOW': return BRAND_COLORS.success;
      default: return BRAND_COLORS.info;
    }
  };

  const progressColor = challenge.progress >= 80 ? BRAND_COLORS.success : 
                       challenge.progress >= 60 ? BRAND_COLORS.warning : BRAND_COLORS.info;

  return (
    <motion.div
      className="p-5 transition-all duration-300 bg-white border border-gray-100 shadow-sm rounded-xl hover:shadow-md"
      whileHover={{ y: -1 }}
    >
      {/* Header with Elite Status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg`} style={{ backgroundColor: `${getDifficultyColor(challenge.difficulty)}20` }}>
            <Trophy className="w-5 h-5" style={{ color: getDifficultyColor(challenge.difficulty) }} />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-bold text-gray-900">{challenge.title}</h3>
              <div className={`px-2 py-0.5 rounded text-xs font-bold`} 
                   style={{ backgroundColor: getDifficultyColor(challenge.difficulty), color: 'white' }}>
                {challenge.difficulty}
              </div>
            </div>
            <p className="text-xs text-gray-600">{challenge.category} • {challenge.type}</p>
          </div>
        </div>
        <div className="text-right">
          <div className={`px-2 py-1 rounded text-xs font-medium`}
               style={{ backgroundColor: `${getUrgencyColor(challenge.urgency)}20`, color: getUrgencyColor(challenge.urgency) }}>
            {challenge.urgency}
          </div>
          <div className="mt-1 text-xs text-gray-500">{challenge.daysLeft}d left</div>
        </div>
      </div>

      {/* Progress & Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center">
          <div className="text-lg font-bold text-gray-900">{challenge.progress}%</div>
          <div className="text-xs text-gray-500">Complete</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center space-x-1">
            <Flame className="w-3 h-3 text-orange-500" />
            <span className="text-lg font-bold text-orange-600">{challenge.streak}</span>
          </div>
          <div className="text-xs text-gray-500">Day Streak</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-green-600">{challenge.reward}</div>
          <div className="text-xs text-gray-500">MP Reward</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="w-full h-2 bg-gray-200 rounded-full">
          <motion.div
            className="h-2 rounded-full"
            style={{ backgroundColor: progressColor }}
            initial={{ width: 0 }}
            animate={{ width: `${challenge.progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Elite Status Info */}
      <div className="p-3 mb-4 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center justify-between mb-1 text-xs">
          <span className="font-medium text-gray-700">Participants</span>
          <span className="font-bold text-gray-900">{challenge.participants?.toLocaleString()}/{challenge.maxParticipants?.toLocaleString()}</span>
        </div>
        <div className="w-full h-1 bg-gray-200 rounded-full">
          <div 
            className="h-1 rounded-full bg-gradient-to-r from-blue-500 to-purple-500"
            style={{ width: `${(challenge.participants / challenge.maxParticipants) * 100}%` }}
          />
        </div>
      </div>

      {/* Health Benefit */}
      <div className="mb-4">
        <div className="flex items-start space-x-2">
          <Activity className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-600">{challenge.healthBenefit}</p>
        </div>
      </div>

      {/* Action & Next Steps */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600">Next Action:</span>
          <span className="text-xs font-medium text-gray-900">{challenge.nextAction}</span>
        </div>
        <button className="w-full px-3 py-2 text-sm font-medium text-white transition-all duration-300 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
          Continue Elite Challenge
        </button>
      </div>

      {/* Eligibility Notice */}
      <div className="pt-3 mt-3 border-t border-gray-100">
        <div className="flex items-center space-x-1">
          <Shield className="w-3 h-3 text-blue-500" />
          <span className="text-xs font-medium text-blue-600">{challenge.eligibility}</span>
        </div>
      </div>
    </motion.div>
  );
};

// Professional Health Plan Card based on HealthcarePlans.jsx patterns
const ProfessionalHealthPlanCard = ({ plan }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'On Track': return BRAND_COLORS.success;
      case 'Needs Attention': return BRAND_COLORS.warning;
      case 'Critical': return BRAND_COLORS.error;
      default: return BRAND_COLORS.info;
    }
  };

  const getPlanTypeIcon = (type) => {
    switch (type) {
      case 'premium': return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'medical': return <Stethoscope className="w-4 h-4 text-red-500" />;
      case 'standard': return <Shield className="w-4 h-4 text-blue-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <motion.div
      className="p-5 transition-all duration-300 bg-white border border-gray-100 shadow-sm rounded-xl hover:shadow-md"
      whileHover={{ y: -1 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg`} style={{ backgroundColor: `${getStatusColor(plan.status)}20` }}>
            {getPlanTypeIcon(plan.planType)}
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">{plan.name}</h3>
            <p className="text-xs text-gray-600">{plan.category} • {plan.phase}</p>
          </div>
        </div>
        <div className="text-right">
          <div className={`px-2 py-1 rounded text-xs font-medium`}
               style={{ backgroundColor: `${getStatusColor(plan.status)}20`, color: getStatusColor(plan.status) }}>
            {plan.status}
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center">
          <div className="text-lg font-bold text-gray-900">{plan.compliance}%</div>
          <div className="text-xs text-gray-500">Compliance</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center space-x-1">
            <Calendar className="w-3 h-3 text-green-500" />
            <span className="text-lg font-bold text-green-600">{plan.adherenceStreak}</span>
          </div>
          <div className="text-xs text-gray-500">Day Streak</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-600">{plan.progressPercentage}%</div>
          <div className="text-xs text-gray-500">Overall</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1 text-xs">
          <span className="text-gray-600">Plan Progress</span>
          <span className="font-medium">{plan.estimatedCompletion} remaining</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full">
          <motion.div
            className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-green-500"
            initial={{ width: 0 }}
            animate={{ width: `${plan.progressPercentage}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Key Metrics */}
      {plan.metrics && (
        <div className="p-3 mb-4 rounded-lg bg-gray-50">
          <h4 className="mb-2 text-xs font-medium text-gray-900">Current Metrics</h4>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(plan.metrics).map(([key, value]) => (
              <div key={key} className="text-center">
                <div className="text-sm font-bold text-gray-900">{value}</div>
                <div className="text-xs text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk Flags */}
      {plan.riskFlags.length > 0 && (
        <div className="p-3 mb-4 border border-orange-200 rounded-lg bg-orange-50">
          <div className="flex items-center mb-1 space-x-2">
            <AlertTriangle className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-medium text-orange-800">Attention Required</span>
          </div>
          {plan.riskFlags.map((flag, index) => (
            <p key={index} className="text-xs text-orange-700">• {flag}</p>
          ))}
        </div>
      )}

      {/* Recent Insights */}
      <div className="mb-4 space-y-2">
        <h4 className="text-xs font-medium text-gray-900">Recent Insights</h4>
        {plan.insights.slice(0, 2).map((insight, index) => (
          <div key={index} className="flex items-start space-x-2">
            <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-600">{insight}</p>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600">Last Task:</span>
          <span className="font-medium text-gray-900">{plan.lastTask}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600">Next Task:</span>
          <span className="font-medium text-gray-900">{plan.nextTask}</span>
        </div>
        <button className="w-full px-3 py-2 text-sm font-medium text-white transition-colors rounded-lg bg-navy hover:bg-navy/90"
                style={{ backgroundColor: BRAND_COLORS.navy }}>
          Continue Plan
        </button>
      </div>
    </motion.div>
  );
};

// Sample Preventative Plan Data
const preventativePlanData = {
  risk: {
    id: "risk_prediabetes_001",
    name: "Prediabetes Risk (early signal)",
    metricLabel: "Fasting glucose",
    latestValue: "107 mg/dL (user-supplied)",
    lastSyncAt: "2025-10-07T13:15:00Z",
    source: "Manual",
    trend7d: [
      { t: "2025-10-01T00:00:00Z", v: 106 },
      { t: "2025-10-02T00:00:00Z", v: 108 },
      { t: "2025-10-03T00:00:00Z", v: 107 },
      { t: "2025-10-04T00:00:00Z", v: 106 },
      { t: "2025-10-05T00:00:00Z", v: 107 },
      { t: "2025-10-06T00:00:00Z", v: 107 },
      { t: "2025-10-07T00:00:00Z", v: 107 }
    ],
    status: "early_signal",
    rationale: "Recent fasting readings trend above your preferred target band and activity dipped last week."
  },
  tasks: [
    {
      id: "t_steps",
      title: "Walk 3,000 steps/day",
      frequency: "daily",
      durationDays: 120,
      details: "Light daily movement helps support insulin sensitivity and energy balance.",
      target: { exact: 3000, unit: "steps" },
      progress: { doneDays: 0, streak: 0 },
      enabled: true
    },
    {
      id: "t_nutrition",
      title: "Reduce added sugar & refined carbs",
      frequency: "daily",
      durationDays: 120,
      details: "Swap in whole grains, legumes, vegetables, and lean proteins; aim for fiber-rich meals once per day.",
      enabled: true
    },
    {
      id: "t_hydration",
      title: "Hydrate 7 glasses/day",
      frequency: "daily",
      durationDays: 90,
      details: "Adequate hydration can support appetite regulation and daily energy.",
      target: { exact: 7, unit: "glasses" },
      progress: { doneDays: 0, streak: 0 },
      enabled: true
    },
    {
      id: "t_acv",
      title: "ACV: 1 tsp in water every other day",
      frequency: "every_other_day",
      durationDays: 60,
      details: "Optional habit; some people use diluted ACV as part of a healthy routine.",
      caution: "Dilute well; may affect tooth enamel or reflux—skip if irritating.",
      target: { exact: 1, unit: "tsp" },
      enabled: false
    },
    {
      id: "t_sleep",
      title: "Consistent sleep 7–8 hours",
      frequency: "daily",
      durationDays: 120,
      details: "Stable sleep and wind-down routine can aid appetite and glucose control proxies.",
      target: { min: 7, max: 8, unit: "hours" },
      enabled: true
    }
  ],
  forecastDays: 120,
  personalization: {
    dietStyle: "Mediterranean",
    acvEnabled: false,
    stepGoal: 3000,
    hydrationGlasses: 7
  },
  safetyText: "This plan is educational and lifestyle-focused. It is not medical advice. If you have health concerns, consult a clinician.",
  privacy: "private_only"
};

// Sparkline Component for Trend Visualization
const Sparkline = ({ data, width = 80, height = 24 }) => {
  if (!data || data.length === 0) return null;

  const values = data.map(d => d.v);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values.map((value, index) => {
    const x = (index / (values.length - 1)) * width;
    const y = height - ((value - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} className="text-orange-500">
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        points={points}
        className="opacity-80"
      />
      {values.map((value, index) => {
        const x = (index / (values.length - 1)) * width;
        const y = height - ((value - min) / range) * height;
        return (
          <circle
            key={index}
            cx={x}
            cy={y}
            r="1.5"
            fill="currentColor"
            className="opacity-60"
          />
        );
      })}
    </svg>
  );
};

// Progress Ring Component
const ProgressRing = ({ progress, size = 40, strokeWidth = 3 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = `${circumference} ${circumference}`;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          className="text-gray-200"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          className="text-teal-500 transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-semibold text-gray-700">{progress}%</span>
      </div>
    </div>
  );
};

// Main Preventative Plan Card Component
const PreventativePlanCard = ({ data }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [taskStates, setTaskStates] = useState(
    data.tasks.reduce((acc, task) => ({
      ...acc,
      [task.id]: task.enabled
    }), {})
  );

  const enabledTasks = data.tasks.filter(task => taskStates[task.id]);
  const totalProgress = enabledTasks.length > 0 ? 
    (enabledTasks.reduce((sum, task) => sum + (task.progress?.doneDays || 0), 0) / 
     (enabledTasks.length * (data.forecastDays / 30))) * 100 : 0;

  const getStatusColor = (status) => {
    switch (status) {
      case 'early_signal': return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700' };
      case 'moderate_concern': return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700' };
      default: return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' };
    }
  };

  const statusColors = getStatusColor(data.risk.status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="overflow-hidden border border-orange-200 shadow-2xl bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 rounded-3xl"
    >
      {/* Colorful Header with Gradient */}
      <div className="p-6 text-white bg-gradient-to-r from-red-600 via-orange-500 to-amber-500">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-3 py-1 text-xs font-bold border rounded-full bg-red-500/80 backdrop-blur-sm border-white/20">
                  ⚠️ EARLY HEALTH ALERT
                </span>
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-white/20 backdrop-blur-sm">
                  Smart Device Detected
                </span>
              </div>
              <h2 className="mb-1 text-2xl font-bold">Prediabetic Condition Detected</h2>
              <p className="text-sm font-medium opacity-90">
                Your smart devices detected early signs of diabetes risk before symptoms appear
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="mb-1 text-sm opacity-90">Detection Status</div>
            <div className="flex items-center gap-1 px-3 py-1 text-lg font-bold rounded-lg bg-white/20 backdrop-blur-sm">
              <Smartphone className="w-4 h-4" />
              Live Monitoring
            </div>
          </div>
        </div>
        
        {/* Key Detection Summary */}
        <div className="p-4 mt-4 border bg-white/10 backdrop-blur-sm rounded-xl border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">🔍 What We Found</h3>
              <p className="text-sm opacity-90">Blood glucose patterns indicate prediabetic range</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">105 mg/dL</div>
              <div className="text-xs opacity-80">Fasting Glucose (Normal: &lt;100)</div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Detection Summary */}
        <div className="grid grid-cols-1 gap-6 mb-6 lg:grid-cols-2">
          {/* Left: Early Detection Summary */}
          <div className="p-5 border border-red-200 bg-gradient-to-br from-red-100 to-orange-100 rounded-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">🏥 Early Health Detection</h3>
              <div className="px-3 py-1 text-sm font-bold text-red-700 bg-red-100 border border-red-300 rounded-full">
                Prediabetic Stage
              </div>
            </div>
            
            <div className="mb-4">
              <div className="mb-1 text-sm text-gray-600">Detected Condition</div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl font-bold text-red-700">Prediabetes</span>
                <TrendingUp className="w-4 h-4 text-orange-500" />
              </div>
              <div className="mb-3 text-xs text-gray-500">
                Detected automatically via smart glucose monitoring • No symptoms yet
              </div>
              <div className="p-3 border border-yellow-200 rounded-lg bg-yellow-50">
                <div className="mb-1 text-sm font-medium text-yellow-800">
                  ✨ Great News: Early Detection Advantage
                </div>
                <div className="text-xs text-yellow-700">
                  Caught 2-5 years before symptoms typically appear. Reversible with lifestyle changes.
                </div>
              </div>
            </div>

            {/* Mini Sparkline */}
            <div className="mb-4">
              <div className="mb-2 text-sm text-gray-600">📊 7-day glucose pattern from smart device</div>
              <div className="flex items-end justify-between h-12 gap-1">
                {data.risk.trend7d.map((point, index) => (
                  <div
                    key={index}
                    className="rounded-t bg-gradient-to-t from-orange-400 to-orange-300"
                    style={{
                      height: `${(point.v / Math.max(...data.risk.trend7d.map(p => p.v))) * 100}%`,
                      minHeight: '8px',
                      width: '100%'
                    }}
                  />
                ))}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Trending upward - Early intervention recommended
              </div>
            </div>
          </div>

          {/* Right: What This Means */}
          <div className="p-5 border border-blue-200 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl">
            <h3 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
              <Info className="w-5 h-5 text-blue-600" />
              💡 What This Means For You
            </h3>
            
            <div className="space-y-4">
              <div className="p-3 border border-green-200 rounded-lg bg-green-50">
                <div className="mb-1 text-sm font-medium text-green-800">
                  🎯 Early Detection = Better Outcomes
                </div>
                <div className="text-xs text-green-700">
                  Studies show 90% success rate in reversing prediabetes when caught early
                </div>
              </div>
              
              <div className="p-3 border border-blue-200 rounded-lg bg-blue-50">
                <div className="mb-1 text-sm font-medium text-blue-800">
                  🔬 How We Detected This
                </div>
                <div className="text-xs text-blue-700">
                  Continuous glucose monitoring via smart devices detected patterns before clinical symptoms
                </div>
              </div>
              
              <div className="p-3 border border-purple-200 rounded-lg bg-purple-50">
                <div className="mb-1 text-sm font-medium text-purple-800">
                  📈 Your Advantage
                </div>
                <div className="text-xs text-purple-700">
                  Most people discover diabetes 2-5 years after it develops. You're ahead of the curve!
                </div>
              </div>
            </div>
            
            {/* Timeline & Forecast */}
            <div className="p-4 mt-4 bg-white/60 backdrop-blur-sm rounded-xl">
              <h4 className="mb-2 text-sm font-medium text-gray-700">🎯 Reversal Timeline</h4>
              <p className="mb-3 text-sm text-gray-600">
                Estimated time to reverse prediabetes: ~{data.forecastDays} days with smart monitoring
              </p>
              <div className="w-full h-2 bg-gray-200 rounded-full">
                <div 
                  className="h-2 transition-all duration-1000 rounded-full bg-gradient-to-r from-green-500 to-blue-500"
                  style={{ width: `${Math.min(totalProgress, 100)}%` }}
                />
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Success rate increases significantly with early detection like yours
              </div>
            </div>
          </div>
        </div>

        {/* Lifestyle Tasks - Smart Device Monitored */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <CheckCircle className="w-5 h-5 text-green-600" />
              🎯 Prediabetes Reversal Plan
            </h3>
            <div className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded-full">
              Clinically Proven to Reverse Prediabetes
            </div>
          </div>
          <div className="p-4 mb-4 border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
            <div className="mb-1 text-sm font-medium text-green-800">
              ✨ Smart Device Integration Advantage
            </div>
            <div className="text-xs text-green-700">
              Each task is automatically monitored through your smart devices. Real-time feedback helps optimize your progress and increases success rates by 73%.
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {data.tasks.map((task) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="p-4 transition-all duration-200 border-2 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200 rounded-xl hover:shadow-md"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-white rounded-lg shadow-sm">
                      {task.id === 't_acv' ? (
                        <Apple className="w-5 h-5 text-yellow-600" />
                      ) : task.id === 't_steps' ? (
                        <Activity className="w-5 h-5 text-blue-600" />
                      ) : task.id === 't_sleep' ? (
                        <Moon className="w-5 h-5 text-indigo-600" />
                      ) : task.id === 't_hydration' ? (
                        <Waves className="w-5 h-5 text-cyan-600" />
                      ) : task.id === 't_nutrition' ? (
                        <Utensils className="w-5 h-5 text-green-600" />
                      ) : (
                        <Target className="w-5 h-5 text-purple-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {task.id === 't_acv' 
                          ? task.title.replace('ACV', 'Apple Cider Vinegar (ACV)')
                          : task.title
                        }
                      </h4>
                      <div className="text-sm text-gray-600">
                        {task.frequency} • {task.durationDays} days • Smart monitored
                      </div>
                    </div>
                  </div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={taskStates[task.id]}
                      onChange={(e) => setTaskStates(prev => ({
                        ...prev,
                        [task.id]: e.target.checked
                      }))}
                      className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
                    />
                  </label>
                </div>
                
                <p className="mb-3 text-sm text-gray-700">
                  {task.id === 't_acv' 
                    ? task.details.replace('ACV', 'Apple Cider Vinegar (ACV)')
                    : task.details
                  }
                </p>
                
                {task.caution && (
                  <div className="flex items-start gap-2 p-3 mb-3 border border-yellow-200 rounded-lg bg-yellow-50">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-yellow-700">{task.caution}</p>
                  </div>
                )}

                {task.progress && (
                  <div className="p-3 rounded-lg bg-white/60 backdrop-blur-sm">
                    <div className="flex justify-between mb-1 text-xs text-gray-600">
                      <span>Progress</span>
                      <span>{task.progress.doneDays} days • {task.progress.streak} streak</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className="h-1.5 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full"
                        style={{ width: `${(task.progress.doneDays / task.durationDays) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-4 mb-6 sm:flex-row">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 px-6 py-3 font-bold text-white transition-all duration-200 shadow-lg bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl hover:from-green-700 hover:to-emerald-700"
          >
            🎯 Start Prediabetes Reversal Program
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 px-6 py-3 font-semibold text-blue-700 transition-all duration-200 border border-blue-300 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl hover:from-blue-200 hover:to-indigo-200"
          >
            📋 View Detailed Analysis
          </motion.button>
        </div>

        {/* Success Statistics */}
        <div className="p-4 mb-6 border border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
          <div className="text-center">
            <h4 className="mb-2 text-lg font-bold text-purple-800">🏆 Early Detection Success Rates</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">90%</div>
                <div className="text-xs text-gray-600">Reversal Success with Early Detection</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">73%</div>
                <div className="text-xs text-gray-600">Improvement with Smart Monitoring</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">2-5 yrs</div>
                <div className="text-xs text-gray-600">Head Start vs Traditional Detection</div>
              </div>
            </div>
          </div>
        </div>

        {/* Safety & Privacy Footer */}
        <div className="p-4 border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="mb-1 text-sm font-medium text-blue-800">📋 Medical Disclaimer & Privacy</div>
              <p className="mb-2 text-sm text-blue-700">
                This prediabetes detection is based on smart device data patterns and should be confirmed with your healthcare provider. 
                Early detection gives you a significant advantage in preventing Type 2 diabetes progression.
              </p>
              <div className="flex items-center gap-4 text-xs text-blue-600">
                <span className="flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Private & Confidential
                </span>
                <span className="flex items-center gap-1">
                  <Smartphone className="w-3 h-3" />
                  Smart device verified
                </span>
                <span className="flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  HIPAA Compliant
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Professional Rewards & Wallet Card
const ProfessionalRewardsCard = ({ type, title, balance, rate, action }) => (
  <motion.div
    layout
    className="p-5 transition-all duration-300 border border-yellow-200 shadow-sm bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl hover:shadow-md"
    whileHover={{ y: -1 }}
  >
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center space-x-3">
        <div className="p-2 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-400">
          {type === 'points' ? (
            <Zap className="w-5 h-5 text-white" />
          ) : (
            <Coins className="w-5 h-5 text-white" />
          )}
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-600">Earn Rate: {rate}</p>
        </div>
      </div>
    </div>

    <div className="mb-2 text-2xl font-bold text-gray-900">
      {balance.toLocaleString()}
    </div>

    <div className="grid grid-cols-2 gap-3 mb-4">
      <div className="p-2 text-center bg-white rounded-lg">
        <div className="text-sm font-bold text-green-600">+{Math.floor(Math.random() * 500 + 200)}</div>
        <div className="text-xs text-gray-500">Last 7d</div>
      </div>
      <div className="p-2 text-center bg-white rounded-lg">
        <div className="text-sm font-bold text-blue-600">+{Math.floor(Math.random() * 200 + 100)}</div>
        <div className="text-xs text-gray-500">Today</div>
      </div>
    </div>

    <button className="w-full px-3 py-2 text-sm font-medium text-white transition-all duration-300 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600">
      {action}
    </button>
  </motion.div>
);

const UserDashboard = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState('personal');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsRefreshing(false);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <MediCureOnSidebar onNavigate={onNavigate} activePage="dashboard" />
      
      <div className="flex-1 overflow-auto">
        <MediCureOnHeader
          title="Enterprise Health Analytics Dashboard"
          subtitle="Professional VSM System with Elite Challenge Integration"
          showRefresh={true}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
        />
        
        <div className="p-6">
          {/* Tab Navigation */}
          <div className="flex max-w-md p-1 mb-6 space-x-1 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.navy}15` }}>
            <button
              onClick={() => setActiveTab('personal')}
              className={`flex-1 px-6 py-3 rounded-md text-sm font-bold transition-all duration-300 ${
                activeTab === 'personal'
                  ? 'shadow-lg transform scale-105'
                  : 'hover:bg-white/50'
              }`}
              style={{ 
                backgroundColor: activeTab === 'personal' ? BRAND_COLORS.navy : 'transparent',
                color: activeTab === 'personal' ? 'white' : BRAND_COLORS.navy,
                border: activeTab === 'personal' ? 'none' : `2px solid ${BRAND_COLORS.navy}20`
              }}
            >
              <Lock className="inline w-4 h-4 mr-2" />
              Personal Wellness
            </button>
            <button
              onClick={() => setActiveTab('public')}
              className={`flex-1 px-6 py-3 rounded-md text-sm font-bold transition-all duration-300 ${
                activeTab === 'public'
                  ? 'shadow-lg transform scale-105'
                  : 'hover:bg-white/50'
              }`}
              style={{ 
                backgroundColor: activeTab === 'public' ? BRAND_COLORS.gold : 'transparent',
                color: activeTab === 'public' ? 'white' : BRAND_COLORS.gold,
                border: activeTab === 'public' ? 'none' : `2px solid ${BRAND_COLORS.gold}20`
              }}
            >
              <Globe className="inline w-4 h-4 mr-2" />
              Public Profile
            </button>
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === 'personal' ? (
              <motion.div
                key="personal"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
              >
                {/* Development Notice Banner */}
                <div className="p-4 text-white border-l-4 border-blue-400 shadow-lg bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-full bg-white/20">
                      <Info className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="mb-1 text-lg font-bold">Under Development - Personal Wellness Dashboard</h3>
                      <p className="text-sm text-blue-100">
                        Data shown is for demonstration purposes only. This comprehensive health monitoring system will be LIVE in future phases with real-time vital sign tracking, AI-powered insights, and personalized health recommendations.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Vital Sign Monitoring (VSM) System - Green Theme */}
                <section>
                  <div className="p-6 shadow-2xl bg-gradient-to-br from-emerald-900 via-green-800 to-emerald-900 rounded-3xl">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h1 className="mb-2 text-3xl font-bold text-white">Vital Sign Monitoring (VSM)</h1>
                        <p className="flex items-center gap-2 text-emerald-200">
                          Live & historical health monitoring with AI insights
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs text-green-300 rounded-full bg-green-500/20">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            Live from 3 devices
                          </span>
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-white">Data Quality</div>
                        <div className="flex items-center gap-2 text-2xl font-bold text-white">
                          94% Complete
                          <RefreshCw className="w-5 h-5 text-emerald-300" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
                      {/* Overall Health Score - Large Card */}
                      <div className="lg:col-span-1">
                        <div className="p-6 border bg-emerald-800/30 backdrop-blur-sm rounded-2xl border-emerald-700/30">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-white">Overall Health Score</h3>
                            <Shield className="w-6 h-6 text-emerald-300" />
                          </div>
                          <p className="mb-4 text-sm text-emerald-200">Last sync: 2 min ago</p>
                          
                          {/* Circular Progress */}
                          <div className="flex flex-col items-center mb-6">
                            <div className="relative w-32 h-32">
                              <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 36 36">
                                <path
                                  d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
                                  fill="none"
                                  stroke="rgba(16, 185, 129, 0.3)"
                                  strokeWidth="2"
                                />
                                <path
                                  d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
                                  fill="none"
                                  stroke="#10B981"
                                  strokeWidth="2"
                                  strokeDasharray="92, 100"
                                  className="transition-all duration-1000 ease-out"
                                />
                              </svg>
                              <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <div className="text-4xl font-bold text-white">92%</div>
                                <div className="font-medium text-green-300">Excellent</div>
                              </div>
                            </div>
                          </div>

                          {/* Health Categories */}
                          <div className="mb-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-emerald-200">Cardiovascular</span>
                              <span className="font-semibold text-white">95%</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-emerald-200">Metabolic</span>
                              <span className="font-semibold text-white">88%</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-emerald-200">Sleep Quality</span>
                              <span className="font-semibold text-white">91%</span>
                            </div>
                          </div>

                          {/* Data Completeness */}
                          <div className="mt-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-emerald-200">Data Completeness</span>
                              <span className="text-sm font-medium text-white">94%</span>
                            </div>
                            <div className="w-full h-2 rounded-full bg-emerald-900/50">
                              <div className="h-2 bg-green-400 rounded-full" style={{ width: '94%' }}></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Vital Signs Grid */}
                      <div className="lg:col-span-3">
                        {/* Primary Cardiovascular Row */}
                        <div className="grid grid-cols-2 gap-4 mb-4 lg:grid-cols-4">
                          {/* Heart Rate */}
                          <div className="p-4 transition-all border bg-emerald-700/40 backdrop-blur-sm rounded-xl border-emerald-600/40 hover:bg-emerald-700/50">
                            <div className="flex items-center justify-between mb-2">
                              <Heart className="w-5 h-5 text-red-400" />
                              <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
                            </div>
                            <div className="mb-1 text-sm text-emerald-200">Heart Rate</div>
                            <div className="mb-1 text-2xl font-bold text-white">72 <span className="text-sm font-normal text-emerald-300">bpm</span></div>
                            <div className="text-xs text-emerald-300">Resting • Zone 1</div>
                          </div>

                          {/* HRV */}
                          <div className="p-4 transition-all border bg-emerald-700/40 backdrop-blur-sm rounded-xl border-emerald-600/40 hover:bg-emerald-700/50">
                            <div className="flex items-center justify-between mb-2">
                              <Activity className="w-5 h-5 text-blue-400" />
                              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                            </div>
                            <div className="mb-1 text-sm text-emerald-200">HRV</div>
                            <div className="mb-1 text-2xl font-bold text-white">45 <span className="text-sm font-normal text-emerald-300">ms</span></div>
                            <div className="text-xs text-emerald-300">Good recovery</div>
                          </div>

                          {/* Blood Pressure */}
                          <div className="p-4 transition-all border bg-emerald-700/40 backdrop-blur-sm rounded-xl border-emerald-600/40 hover:bg-emerald-700/50">
                            <div className="flex items-center justify-between mb-2">
                              <Activity className="w-5 h-5 text-purple-400" />
                              <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                            </div>
                            <div className="mb-1 text-sm text-emerald-200">Blood Pressure</div>
                            <div className="mb-1 text-2xl font-bold text-white">118/76</div>
                            <div className="text-xs text-emerald-300">Optimal range</div>
                          </div>

                          {/* SpO2 */}
                          <div className="p-4 transition-all border bg-emerald-700/40 backdrop-blur-sm rounded-xl border-emerald-600/40 hover:bg-emerald-700/50">
                            <div className="flex items-center justify-between mb-2">
                              <Droplets className="w-5 h-5 text-cyan-400" />
                              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
                            </div>
                            <div className="mb-1 text-sm text-emerald-200">SpO₂</div>
                            <div className="mb-1 text-2xl font-bold text-white">98 <span className="text-sm font-normal text-emerald-300">%</span></div>
                            <div className="text-xs text-emerald-300">Excellent</div>
                          </div>
                        </div>

                        {/* Secondary Vitals Row */}
                        <div className="grid grid-cols-2 gap-4 mb-4 lg:grid-cols-4">
                          {/* Respiratory Rate */}
                          <div className="p-4 transition-all border bg-emerald-700/40 backdrop-blur-sm rounded-xl border-emerald-600/40 hover:bg-emerald-700/50">
                            <div className="flex items-center justify-between mb-2">
                              <Activity className="w-5 h-5 text-indigo-400" />
                              <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse"></div>
                            </div>
                            <div className="mb-1 text-sm text-emerald-200">Respiratory Rate</div>
                            <div className="mb-1 text-2xl font-bold text-white">16 <span className="text-sm font-normal text-emerald-300">rpm</span></div>
                            <div className="text-xs text-emerald-300">Normal range</div>
                          </div>

                          {/* Temperature */}
                          <div className="p-4 transition-all border bg-emerald-700/40 backdrop-blur-sm rounded-xl border-emerald-600/40 hover:bg-emerald-700/50">
                            <div className="flex items-center justify-between mb-2">
                              <Thermometer className="w-5 h-5 text-orange-400" />
                              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                            </div>
                            <div className="mb-1 text-sm text-emerald-200">Temperature</div>
                            <div className="mb-1 text-2xl font-bold text-white">98.6 <span className="text-sm font-normal text-emerald-300">°F</span></div>
                            <div className="text-xs text-emerald-300">Core body temp</div>
                          </div>

                          {/* Glucose */}
                          <div className="p-4 transition-all border bg-emerald-700/40 backdrop-blur-sm rounded-xl border-emerald-600/40 hover:bg-emerald-700/50">
                            <div className="flex items-center justify-between mb-2">
                              <Droplets className="w-5 h-5 text-yellow-400" />
                              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                            </div>
                            <div className="mb-1 text-sm text-emerald-200">Glucose</div>
                            <div className="mb-1 text-2xl font-bold text-white">95 <span className="text-sm font-normal text-emerald-300">mg/dL</span></div>
                            <div className="text-xs text-emerald-300">Fasting • Normal</div>
                          </div>

                          {/* Weight/BMI */}
                          <div className="p-4 transition-all border bg-emerald-700/40 backdrop-blur-sm rounded-xl border-emerald-600/40 hover:bg-emerald-700/50">
                            <div className="flex items-center justify-between mb-2">
                              <TrendingUp className="w-5 h-5 text-green-400" />
                              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            </div>
                            <div className="mb-1 text-sm text-emerald-200">Weight | BMI</div>
                            <div className="mb-1 text-2xl font-bold text-white">165 <span className="text-sm font-normal text-emerald-300">lbs</span></div>
                            <div className="text-xs text-emerald-300">BMI 22.4 • Normal</div>
                          </div>
                        </div>

                        {/* Body Composition & Activity Row */}
                        <div className="grid grid-cols-1 gap-4 mb-4 lg:grid-cols-3">
                          {/* Body Composition */}
                          <div className="p-4 border bg-emerald-700/40 backdrop-blur-sm rounded-xl border-emerald-600/40">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="p-2 rounded-lg bg-emerald-600">
                                <Activity className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <div className="font-semibold text-white">Body Composition</div>
                                <div className="text-sm text-emerald-200">Body Fat 12% • Muscle 42%</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="text-emerald-200">Fat: <span className="font-semibold text-white">12%</span></div>
                              <div className="text-emerald-200">Muscle: <span className="font-semibold text-white">42%</span></div>
                            </div>
                          </div>

                          {/* Sleep */}
                          <div className="p-4 border bg-emerald-700/40 backdrop-blur-sm rounded-xl border-emerald-600/40">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="p-2 bg-indigo-600 rounded-lg">
                                <Moon className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <div className="font-semibold text-white">Sleep Quality</div>
                                <div className="text-sm text-emerald-200">7h 32m • 89% efficiency</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="text-emerald-200">Deep: <span className="font-semibold text-white">1h 45m</span></div>
                              <div className="text-emerald-200">REM: <span className="font-semibold text-white">1h 52m</span></div>
                            </div>
                          </div>

                          {/* Activity Summary */}
                          <div className="p-4 border bg-emerald-700/40 backdrop-blur-sm rounded-xl border-emerald-600/40">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="p-2 bg-orange-600 rounded-lg">
                                <Zap className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <div className="font-semibold text-white">Activity Summary</div>
                                <div className="text-sm text-emerald-200">9,247 steps • 45 active min</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="text-emerald-200">Floors: <span className="font-semibold text-white">12</span></div>
                              <div className="text-emerald-200">Calories: <span className="font-semibold text-white">2,247</span></div>
                            </div>
                          </div>
                        </div>

                        {/* Connected Devices & Trends */}
                        <div className="p-4 border bg-emerald-700/40 backdrop-blur-sm rounded-xl border-emerald-600/40">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Smartphone className="w-5 h-5 text-emerald-300" />
                              <div>
                                <div className="font-medium text-white">Connected Devices</div>
                                <div className="text-sm text-emerald-200">Apple Watch Series 9, iPhone HealthKit, Withings Scale, Dexcom G7</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <button className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700 transition-colors">
                                View Trends
                              </button>
                              <button className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors">
                                Export Data
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Active Challenges & Healthcare Plans - Side by Side Layout */}
                <section>
                  <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                    
                    {/* Active Challenges */}
                    <div>
                      <div className="relative mb-6">
                        <h2 className="mb-2 text-2xl font-bold text-transparent bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text">
                          Active Challenges
                        </h2>
                        <div className="w-20 h-1 rounded-full bg-gradient-to-r from-orange-500 to-red-500"></div>
                      </div>
                      
                      <div className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
                        {/* Challenge Summary Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-purple-600" />
                            <span className="font-semibold text-gray-900">3 Active</span>
                          </div>
                          <Trophy className="w-5 h-5 text-yellow-500" />
                        </div>

                        {/* Active Challenge Summary */}
                        <div className="p-3 mb-4 border border-purple-100 bg-purple-50 rounded-xl">
                          <div className="mb-1 text-sm font-medium text-purple-700">Active:</div>
                          <div className="text-sm text-purple-600">Step Streak, Hydration Initiate, Heart Rate Calm</div>
                        </div>

                        {/* Challenge Cards */}
                        <div className="space-y-3">
                          {/* Step Streak */}
                          <div className="flex items-center gap-3 p-3 text-white rounded-lg bg-gradient-to-r from-purple-500 to-purple-600">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/20">
                              <Activity className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium">Step Streak</div>
                              <div className="text-xs opacity-90">Medium</div>
                            </div>
                            <div className="flex items-center justify-center w-6 h-6 bg-green-500 rounded-full">
                              <CheckCircle className="w-4 h-4 text-white" />
                            </div>
                          </div>

                          {/* Hydration Initiate */}
                          <div className="flex items-center gap-3 p-3 text-white rounded-lg bg-gradient-to-r from-blue-500 to-blue-600">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/20">
                              <Droplets className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium">Hydration Initiate</div>
                              <div className="text-xs opacity-90">Medium</div>
                            </div>
                            <div className="flex items-center justify-center w-6 h-6 bg-green-500 rounded-full">
                              <CheckCircle className="w-4 h-4 text-white" />
                            </div>
                          </div>

                          {/* Heart Rate Calm */}
                          <div className="flex items-center gap-3 p-3 text-white rounded-lg bg-gradient-to-r from-green-500 to-green-600">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/20">
                              <Heart className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium">Heart Rate Calm</div>
                              <div className="text-xs opacity-90">Medium</div>
                            </div>
                            <div className="flex items-center justify-center w-6 h-6 bg-yellow-500 rounded-full">
                              <TrendingUp className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        </div>

                        {/* View All Link */}
                        <div className="mt-4 text-center">
                          <button className="text-sm font-medium text-purple-600 hover:text-purple-800">
                            View All Challenges →
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Active Healthcare Plans */}
                    <div>
                      <div className="relative mb-6">
                        <h2 className="mb-2 text-2xl font-bold text-transparent bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text">
                          Active Healthcare Plans
                        </h2>
                        <div className="w-20 h-1 rounded-full bg-gradient-to-r from-blue-500 to-green-500"></div>
                      </div>
                      
                      <div className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
                        {/* Plans Summary Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <Stethoscope className="w-5 h-5 text-blue-600" />
                            <span className="font-semibold text-gray-900">2 Enrolled</span>
                          </div>
                          <Shield className="w-5 h-5 text-green-500" />
                        </div>

                        {/* Healthcare Plan Cards */}
                        <div className="space-y-3">
                          {/* Cardiovascular Excellence */}
                          <div className="p-4 border border-blue-100 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Heart className="w-4 h-4 text-red-500" />
                                <span className="text-sm font-medium text-gray-900">Cardiovascular Excellence</span>
                              </div>
                              <span className="px-2 py-1 text-xs text-green-700 bg-green-100 rounded-full">On Track</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                              <div>Phase 3: Optimization</div>
                              <div>94% Compliance</div>
                              <div>28 Day Streak</div>
                              <div>78% Complete</div>
                            </div>
                          </div>

                          {/* Diabetes Management */}
                          <div className="p-4 border border-orange-100 rounded-xl bg-gradient-to-r from-orange-50 to-yellow-50">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Droplets className="w-4 h-4 text-orange-500" />
                                <span className="text-sm font-medium text-gray-900">Precision Diabetes Management</span>
                              </div>
                              <span className="px-2 py-1 text-xs text-yellow-700 bg-yellow-100 rounded-full">Needs Attention</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                              <div>Phase 2: Stabilization</div>
                              <div>87% Compliance</div>
                              <div>15 Day Streak</div>
                              <div>62% Complete</div>
                            </div>
                          </div>
                        </div>

                        {/* View All Link */}
                        <div className="mt-4 text-center">
                          <button className="text-sm font-medium text-blue-600 hover:text-blue-800">
                            View All Healthcare Plans →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* AI Preventative Plan Section */}
                <section>
                  <PreventativePlanCard data={preventativePlanData} />
                </section>

                {/* Rewards & Digital Wallet - Modern Layout */}
                <section>
                  <div className="relative mb-6">
                    <h2 className="mb-2 text-2xl font-bold text-transparent bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text">
                      Rewards & Digital Wallet
                    </h2>
                    <div className="w-20 h-1 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500"></div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <ProfessionalRewardsCard
                      type="points"
                      title="MediPoints Elite"
                      balance={18750}
                      rate="50-200/action"
                      action="Redeem Premium Rewards"
                    />
                    <ProfessionalRewardsCard
                      type="coin"
                      title="MediCoin Wallet"
                      balance={847}
                      rate="Goal-based mining"
                      action="Open Digital Wallet"
                    />
                    <motion.div className="p-5 transition-all duration-300 border border-purple-200 shadow-sm bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl hover:shadow-md">
                      <h3 className="flex items-center mb-4 text-sm font-bold text-gray-900">
                        <Medal className="w-4 h-4 mr-2 text-purple-600" />
                        Elite Achievement Board
                      </h3>
                      <div className="space-y-3">
                        {[
                          { name: "VSM System Master", date: "Oct 5", icon: "🏆", level: "Legendary" },
                          { name: "Elite Challenge Victor", date: "Oct 3", icon: "⚡", level: "Master" },
                          { name: "Health Plan Champion", date: "Oct 1", icon: "💎", level: "Expert" }
                        ].map((achievement, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-white rounded-lg">
                            <div className="flex items-center space-x-3">
                              <span className="text-lg">{achievement.icon}</span>
                              <div>
                                <span className="text-sm font-medium text-gray-900">{achievement.name}</span>
                                <div className="text-xs font-medium text-purple-600">{achievement.level}</div>
                              </div>
                            </div>
                            <span className="text-xs text-gray-500">{achievement.date}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  </div>
                </section>
              </motion.div>
            ) : (
              <motion.div
                key="public"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
              >
                {/* Development Notice Banner */}
                <div className="p-4 text-white border-l-4 shadow-lg bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl border-amber-400">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-full bg-white/20">
                      <Info className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="mb-1 text-lg font-bold">Under Development - Public Profile System</h3>
                      <p className="text-sm text-amber-100">
                        Data shown is for demonstration purposes only. This community-focused profile system will be LIVE in future phases with real social features, verified achievements, and privacy-compliant fitness sharing (zero PHI/medical data).
                      </p>
                    </div>
                  </div>
                </div>

                {/* A) Public Profile Header */}
                <section>
                  <div className="p-8 shadow-2xl bg-gradient-to-br from-amber-600 via-yellow-500 to-amber-600 rounded-3xl">
                    <div className="flex flex-col items-center mb-8 lg:flex-row lg:items-start lg:space-x-8">
                      {/* Avatar & Basic Info */}
                      <div className="flex flex-col items-center mb-6 lg:mb-0">
                        <div className="relative mb-4">
                          <div className="w-32 h-32 p-1 rounded-full bg-gradient-to-br from-white to-gray-200">
                            <div className="flex items-center justify-center w-full h-full rounded-full bg-amber-100">
                              <span className="text-4xl font-bold" style={{ color: BRAND_COLORS.navy }}>JD</span>
                            </div>
                          </div>
                          <div className="absolute flex items-center justify-center w-8 h-8 text-white bg-green-500 rounded-full -bottom-2 -right-2">
                            <CheckCircle className="w-5 h-5" />
                          </div>
                        </div>
                        <h1 className="mb-2 text-3xl font-bold text-white">John Doe</h1>
                        <p className="mb-2 text-amber-100">@johndoe_health</p>
                        <div className="flex items-center mb-4 text-amber-100">
                          <MapPin className="w-4 h-4 mr-1" />
                          <span>San Francisco, CA</span>
                        </div>
                        <p className="text-sm text-amber-200">Member since March 2024</p>
                      </div>

                      {/* Share Section */}
                      <div className="flex-1 w-full">
                        <div className="p-6 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/20">
                          <h3 className="mb-4 text-xl font-bold text-white">Share Your Fitness Journey</h3>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            {/* Share Link */}
                            <div className="p-4 bg-white/10 rounded-xl">
                              <div className="flex items-center justify-between mb-3">
                                <span className="font-medium text-white">Profile Link</span>
                                <Share2 className="w-5 h-5 text-white" />
                              </div>
                              <div className="p-3 mb-3 rounded-lg bg-white/20">
                                <code className="text-sm text-white">medicureon.com/u/johndoe_health</code>
                              </div>
                              <button className="w-full px-4 py-2 font-medium transition-colors bg-white rounded-lg text-amber-600 hover:bg-gray-100">
                                Copy Link
                              </button>
                            </div>

                            {/* QR Code */}
                            <div className="p-4 bg-white/10 rounded-xl">
                              <div className="flex items-center justify-between mb-3">
                                <span className="font-medium text-white">QR Code</span>
                                <QrCode className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex items-center justify-center p-4 mb-3 bg-white rounded-lg">
                                <div className="flex items-center justify-center w-20 h-20 bg-gray-900 rounded-lg">
                                  <QrCode className="w-12 h-12 text-white" />
                                </div>
                              </div>
                              <button className="w-full px-4 py-2 font-medium transition-colors bg-white rounded-lg text-amber-600 hover:bg-gray-100">
                                Download QR
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* B) Community Metrics */}
                <section>
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 xl:grid-cols-4">
                    
                    {/* Total Steps Card */}
                    <div className="p-6 bg-white border shadow-lg rounded-2xl border-amber-100">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>Total Steps</h3>
                        <Activity className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} />
                      </div>
                      <div className="mb-4">
                        <select className="w-full p-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500">
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold" style={{ color: BRAND_COLORS.navy }}>12,543</div>
                        <div className="text-sm text-gray-600">Today</div>
                        <div className="mt-3 text-sm font-medium text-green-600">↑ 15% from yesterday</div>
                      </div>
                    </div>

                    {/* Completed Challenges */}
                    <div className="p-6 bg-white border shadow-lg rounded-2xl border-amber-100">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>Completed Challenges</h3>
                        <Trophy className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} />
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">30-Day Step Challenge</div>
                            <div className="text-sm text-gray-500">Completed Oct 1, 2025</div>
                          </div>
                          <Medal className="w-5 h-5 text-amber-500" />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">Hydration Hero</div>
                            <div className="text-sm text-gray-500">Completed Sep 28, 2025</div>
                          </div>
                          <Medal className="w-5 h-5 text-blue-500" />
                        </div>
                      </div>
                      <div className="mt-4 text-center">
                        <div className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>24</div>
                        <div className="text-sm text-gray-600">Total Completed</div>
                      </div>
                    </div>

                    {/* Titles & Levels */}
                    <div className="p-6 bg-white border shadow-lg rounded-2xl border-amber-100">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>Titles & Levels</h3>
                        <Crown className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} />
                      </div>
                      <div className="mb-4 text-center">
                        <div className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>Level 23</div>
                        <div className="font-semibold text-amber-600">Community Mentor</div>
                      </div>
                      <div className="mb-4">
                        <div className="flex justify-between mb-1 text-sm text-gray-600">
                          <span>Progress to Level 24</span>
                          <span>780/1000</span>
                        </div>
                        <div className="w-full h-3 bg-gray-200 rounded-full">
                          <div 
                            className="h-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-600" 
                            style={{ width: '78%' }}
                          ></div>
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold" style={{ color: BRAND_COLORS.gold }}>3,250</div>
                        <div className="text-sm text-gray-600">Total Medi Points</div>
                      </div>
                    </div>

                    {/* World Ranking */}
                    <div className="p-6 bg-white border shadow-lg rounded-2xl border-amber-100">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>World Ranking</h3>
                        <BarChart3 className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} />
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-amber-600">#1,847</div>
                        <div className="mb-3 text-sm text-gray-600">Global Rank</div>
                        <div className="px-3 py-1 mb-3 text-sm font-medium text-white rounded-full bg-gradient-to-r from-amber-500 to-amber-600">
                          Top 5%
                        </div>
                        <div className="text-xs text-gray-500">Updated daily at midnight UTC</div>
                      </div>
                    </div>

                    {/* City/Country Challenges */}
                    <div className="p-6 bg-white border shadow-lg rounded-2xl border-amber-100">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>Local Challenges</h3>
                        <Users className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} />
                      </div>
                      <div className="space-y-3">
                        <div className="p-3 rounded-lg bg-gray-50">
                          <div className="font-medium text-gray-900">SF Bay Area League</div>
                          <div className="text-sm text-gray-600">Rank #23 of 1,247</div>
                        </div>
                        <div className="p-3 rounded-lg bg-gray-50">
                          <div className="font-medium text-gray-900">Golden State Guild</div>
                          <div className="text-sm text-gray-600">Member since July 2024</div>
                        </div>
                      </div>
                    </div>

                    {/* Mentorship Impact */}
                    <div className="p-6 bg-white border shadow-lg rounded-2xl border-amber-100">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>Mentorship Impact</h3>
                        <Star className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} />
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>47</div>
                          <div className="text-xs text-gray-600">People Mentored</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-amber-600">152</div>
                          <div className="text-xs text-gray-600">Kudos Received</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">23</div>
                          <div className="text-xs text-gray-600">Testimonials</div>
                        </div>
                      </div>
                    </div>

                    {/* Recent Public Activity */}
                    <div className="p-6 bg-white border shadow-lg rounded-2xl border-amber-100 lg:col-span-2">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold" style={{ color: BRAND_COLORS.navy }}>Recent Public Activity</h3>
                        <Eye className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} />
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100">
                            <Trophy className="w-4 h-4 text-amber-600" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Earned "Consistency Champion" badge</div>
                            <div className="text-sm text-gray-500">October 6, 2025</div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Completed "Weekend Warrior Challenge"</div>
                            <div className="text-sm text-gray-500">October 5, 2025</div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
                            <Award className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Reached Level 23 milestone</div>
                            <div className="text-sm text-gray-500">October 3, 2025</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* C) Social & Safety */}
                <section>
                  <div className="p-8 bg-white border shadow-lg rounded-2xl border-amber-100">
                    <h2 className="mb-6 text-2xl font-bold" style={{ color: BRAND_COLORS.navy }}>Connect & Interact</h2>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                      
                      {/* Follow & Message */}
                      <div className="text-center">
                        <h3 className="mb-4 text-lg font-semibold" style={{ color: BRAND_COLORS.navy }}>Connect</h3>
                        <div className="space-y-3">
                          <button className="flex items-center justify-center w-full px-6 py-3 font-medium text-white transition-all rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700">
                            <UserPlus className="w-5 h-5 mr-2" />
                            Follow
                          </button>
                          <button className="flex items-center justify-center w-full px-6 py-3 font-medium transition-all border-2 rounded-lg border-amber-500 text-amber-600 hover:bg-amber-50">
                            <MessageCircle className="w-5 h-5 mr-2" />
                            Message
                          </button>
                        </div>
                      </div>

                      {/* Community Guidelines */}
                      <div className="text-center">
                        <h3 className="mb-4 text-lg font-semibold" style={{ color: BRAND_COLORS.navy }}>Community Guidelines</h3>
                        <div className="p-4 text-left rounded-lg bg-gray-50">
                          <ul className="space-y-2 text-sm text-gray-600">
                            <li>• No health data disclosures</li>
                            <li>• Fitness aggregates only</li>
                            <li>• Respect privacy & safety</li>
                            <li>• Positive interactions</li>
                          </ul>
                        </div>
                      </div>

                      {/* Safety Actions */}
                      <div className="text-center">
                        <h3 className="mb-4 text-lg font-semibold" style={{ color: BRAND_COLORS.navy }}>Safety</h3>
                        <div className="space-y-3">
                          <button className="flex items-center justify-center w-full px-4 py-2 font-medium text-red-600 transition-all border border-red-300 rounded-lg hover:bg-red-50">
                            <Flag className="w-4 h-4 mr-2" />
                            Report User
                          </button>
                          <button className="w-full px-4 py-2 font-medium text-gray-600 transition-all border border-gray-300 rounded-lg hover:bg-gray-50">
                            Block User
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Content Filter Notice */}
                    <div className="p-4 mt-6 border border-blue-200 rounded-lg bg-blue-50">
                      <div className="flex items-start space-x-3">
                        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-semibold text-blue-900">Privacy & Safety</h4>
                          <p className="mt-1 text-sm text-blue-700">
                            All public content is automatically filtered to prevent health data disclosures. 
                            Only fitness aggregates and community achievements are shared publicly.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;